import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Modal } from './modal'
import { ChevronDown } from 'lucide-react'
import { Segmented } from './segmented'
import { Spinner } from './spinner'
import { NumericInput } from './numeric-input'
import { Toggle } from './toggle'
import { TokenIcon } from './holdings-table'
import { ChainChip, HeaderRule } from './header-chips'
import { coreTokens, holdingToPicked, TokenPicker, type PickedToken } from './token-picker'
import { SOL_MINT, USDC_MINT, USDT_MINT, tokenLogo } from '@/lib/default-tokens'
import { errorMessage, useTRPC } from '@/lib/trpc'
import { useRefreshActivity } from '@/lib/activity-feed'
import { CUSTODY_LABELS, EXCHANGE_IDS, isExchangeId, useCustody, usePoolMode, type ExchangeId } from '@/lib/pool'
import { PlatformGroup } from './platform-chip'
import type { Holding, MeWallet, StrategyTemplate } from '@/lib/api-types'
// type-only: the enums live in backend modules that must not be bundled
import type { Chain } from '../../../src/constants/chains'

const SOLANA = 'solana' as Chain
import { formatAmount, formatDuration, formatMarketCapRange, formatPrice, formatTokenAmount, formatUsd, formatUsdShorthand, parseUsdShorthand } from '@/lib/format'

type Side = 'sell' | 'buy'
type Method = 'volume' | 'dca' | 'limit'
type Unit = 'minute' | 'hour' | 'day'
type Settlement = 'SOL' | 'USDC' | 'USDT'
const UNIT_SECONDS: Record<Unit, number> = { minute: 60, hour: 3600, day: 86400 }
const SOL_FEE_RESERVE = 0.01
const CORE_MINTS = new Set([SOL_MINT, USDC_MINT, USDT_MINT])
const DEFAULT_VARIANCE_PCT = 20

/** shortcut amounts: at most 6 decimals, no trailing zeros */
function trimAmount(value: number) {
  return value.toFixed(6).replace(/\.?0+$/, '')
}

/** decimal string -> base units, without float error */
function toRaw(value: string, decimals: number): bigint | null {
  const match = /^(\d*)(?:\.(\d*))?$/.exec(value.trim())
  if (!match || (match[1] === '' && !match[2])) return null
  const whole = match[1] || '0'
  const frac = (match[2] ?? '').slice(0, decimals).padEnd(decimals, '0')
  const raw = BigInt(whole + frac)
  return raw > 0n ? raw : null
}

/** base units -> decimal string, without float error: the inverse of toRaw, for a duplicate's amount field */
function fromRaw(raw: string, decimals: number): string {
  const digits = raw.padStart(decimals + 1, '0')
  const whole = digits.slice(0, digits.length - decimals)
  const frac = digits.slice(digits.length - decimals).replace(/0+$/, '')
  return frac ? `${whole}.${frac}` : whole
}

/** a price as typed in the target field: plain digits, never 1e-7 */
function plainNumber(value: number): string {
  return value > 0 && value < 1e-6 ? value.toFixed(12).replace(/\.?0+$/, '') : String(value)
}

/** an interval as the wizard's two fields: the largest unit that divides it evenly */
function splitInterval(seconds: number): { every: string; unit: Unit } {
  for (const unit of ['day', 'hour', 'minute'] as const) {
    if (seconds % UNIT_SECONDS[unit] === 0) return { every: String(seconds / UNIT_SECONDS[unit]), unit }
  }
  return { every: String(Math.max(1, Math.round(seconds / 60))), unit: 'minute' }
}

/** "1,500,000" style input for dollar amounts: digits only, commas allowed */
export function StrategyWizard({
  open,
  onClose,
  wallet,
  holdings,
  solBalance,
  initialToken = null,
  template = null,
}: {
  open: boolean
  onClose: () => void
  wallet: MeWallet
  holdings: Holding[]
  solBalance: number | undefined
  /** opened from a holdings row: that token is picked already (John, Oct 31 2026) */
  initialToken?: PickedToken | null
  /** a duplicate (John, Nov 3 2026): every setting of this strategy filled in, the second step open, nothing started until "Set strategy" */
  template?: StrategyTemplate | null
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [step, setStep] = useState<1 | 2>(1)
  const [name, setName] = useState('')
  const [side, setSide] = useState<Side>('sell')
  const [method, setMethod] = useState<Method>('dca')
  const [tokenAddress, setTokenAddress] = useState('')
  // the exchange the strategy belongs to (John, Oct 31 2026): the account's current one until the chip is clicked
  const { custody, custodies, exchange: onExchange } = useCustody()
  const [exchangeId, setExchangeId] = useState<ExchangeId>(isExchangeId(custody) ? custody : (EXCHANGE_IDS.find((id) => custodies[id]) ?? 'mexc'))
  // every exchange this deployment offers, for the header's group; the strategy's own pick, not the account's remembered one
  const exchangeOptions = EXCHANGE_IDS.filter((id) => custodies[id]).map((id) => ({ id, label: CUSTODY_LABELS[id] }))
  useEffect(() => {
    if (open && !template && isExchangeId(custody)) setExchangeId(custody)
  }, [open, custody, template])
  const exchangeBalances = useQuery({ ...trpc.vault.exchangeBalances.queryOptions(), enabled: open && onExchange, refetchInterval: 15_000 })
  const exchangeSol = onExchange && exchangeBalances.data?.[exchangeId] ? Number(exchangeBalances.data[exchangeId]!.SOL) / 1e9 : undefined
  const [settlement, setSettlement] = useState<Settlement>('SOL')
  const [amount, setAmount] = useState('')
  const [every, setEvery] = useState('5')
  const [unit, setUnit] = useState<Unit>('minute')
  const [orders, setOrders] = useState('10')
  const [targetPrice, setTargetPrice] = useState('')
  const [optionalOpen, setOptionalOpen] = useState(false)
  const [maxImpact, setMaxImpact] = useState(10)
  const [marketCapOn, setMarketCapOn] = useState(false)
  const [minMarketCap, setMinMarketCap] = useState('')
  const [maxMarketCap, setMaxMarketCap] = useState('')
  const [randomizeAmount, setRandomizeAmount] = useState(true)
  const [amountVariance, setAmountVariance] = useState(DEFAULT_VARIANCE_PCT)
  const [randomizeInterval, setRandomizeInterval] = useState(true)
  const [intervalVariance, setIntervalVariance] = useState(DEFAULT_VARIANCE_PCT)
  const [scheduledOn, setScheduledOn] = useState(false)
  const [startAt, setStartAt] = useState('')
  const [error, setError] = useState<string | null>(null)

  // the pay/receive tokens a plan can settle in, with their logos
  const settlements = useMemo(() => coreTokens(holdings), [holdings])
  const settlementToken = settlements.find((t) => t.symbol === settlement) ?? settlements[0]
  // what can be sold: tokens in the trading wallet and tokens kept in the vault, except the one received
  const sellable = useMemo(
    () =>
      holdings.filter(
        (t) => t.chain === 'solana' && !t.isNative && Number(t.balance) > 0 && t.address !== settlementToken?.address,
      ),
    [holdings, settlementToken?.address],
  )
  // opened from a holdings row: the token is in place, as a sell when it is held, before the first step asks for the order type and method
  useEffect(() => {
    if (!open || !initialToken) return
    setTokenAddress(initialToken.address)
    setSide(sellable.some((t) => t.address === initialToken.address) ? 'sell' : 'buy')
    setAmount('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialToken?.address])
  // a duplicate: the old strategy's settings, as the wizard would have sent them, with the second step open so the amount
  // and the balance line are seen before "Set strategy". The old start time has passed, so the copy starts now unless a
  // new one is set; the market cap gate opens the optional settings, so it is in view.
  useEffect(() => {
    if (!open || !template) return
    const t = template
    const limit = t.kind === 'limit'
    setStep(2)
    setName(t.name ?? '')
    setSide(t.side === 'buy' ? 'buy' : 'sell')
    setMethod(limit ? 'limit' : 'dca')
    setTokenAddress(t.token.address)
    if (t.exchange && custodies[t.exchange]) setExchangeId(t.exchange)
    const settled = t.settlement.symbol.toUpperCase()
    setSettlement(settled === 'USDC' || settled === 'USDT' ? settled : 'SOL')
    setAmount(fromRaw(t.dca.totalAmountRaw, t.side === 'sell' ? t.token.decimals : t.settlement.decimals))
    if (limit) {
      setTargetPrice(t.limit?.targetPriceUsd ? plainNumber(t.limit.targetPriceUsd) : '')
    } else {
      const interval = splitInterval(t.dca.intervalSeconds)
      setEvery(interval.every)
      setUnit(interval.unit)
      setOrders(String(t.dca.orders))
    }
    setMaxImpact(t.limits.maxPriceImpactPct)
    const gated = t.limits.minMarketCapUsd !== null || t.limits.maxMarketCapUsd !== null
    setMarketCapOn(gated)
    setMinMarketCap(t.limits.minMarketCapUsd !== null ? formatUsdShorthand(t.limits.minMarketCapUsd) : '')
    setMaxMarketCap(t.limits.maxMarketCapUsd !== null ? formatUsdShorthand(t.limits.maxMarketCapUsd) : '')
    setRandomizeAmount(t.execution.randomizeAmount)
    if (t.execution.amountVariancePct !== null) setAmountVariance(t.execution.amountVariancePct)
    setRandomizeInterval(t.execution.randomizeInterval)
    if (t.execution.intervalVariancePct !== null) setIntervalVariance(t.execution.intervalVariancePct)
    setScheduledOn(false)
    setStartAt('')
    setOptionalOpen(gated)
    setError(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, template?.id])
  const selectedHolding = sellable.find((t) => t.address === tokenAddress)
  const fromVault = selectedHolding?.custody === 'vault'
  const lookup = useQuery({
    ...trpc.token.get.queryOptions({ chain: SOLANA, address: tokenAddress }),
    enabled: side === 'buy' && tokenAddress.length >= 32,
    retry: false,
  })
  const lookupToken = lookup.data
  // a token buy never lands in the trading wallet: it runs from a fresh wallet and goes to the vault
  // pool custody: a stablecoin bought with SOL is a position too, it lands in the pool like any token
  const poolMode = usePoolMode()
  const privateBuy = side === 'buy' && !!lookupToken && (poolMode ? tokenAddress !== SOL_MINT : !CORE_MINTS.has(tokenAddress))

  const settlementHolding = holdings.find((t) => t.chain === 'solana' && (settlement === 'SOL' ? t.isNative : t.address === settlementToken?.address))
  const inputDecimals = side === 'sell' ? (selectedHolding?.decimals ?? 0) : (settlementToken?.decimals ?? 9)
  const inputBalance =
    side === 'sell'
      ? selectedHolding
        ? Number(selectedHolding.balance) / 10 ** selectedHolding.decimals
        : 0
      : settlement === 'SOL'
        ? Math.max(0, (exchangeSol ?? solBalance ?? 0) - SOL_FEE_RESERVE)
        : settlementHolding
          ? Number(settlementHolding.balance) / 10 ** settlementHolding.decimals
          : 0
  const inputPriceUsd = side === 'sell' ? selectedHolding?.priceUsd : (settlementHolding?.priceUsd ?? (settlement === 'SOL' ? undefined : 1))
  const inputSymbol = side === 'sell' ? (selectedHolding?.symbol ?? '').toUpperCase() : settlement
  // a limit order's target against the token's price now, and what market cap that is (John, Oct 31 2026)
  const tokenPriceUsd = side === 'sell' ? selectedHolding?.priceUsd : lookupToken?.priceUsd
  const tokenMarketCapUsd = side === 'sell' ? selectedHolding?.marketCapUsd : lookupToken?.marketCapUsd
  const targetNumber = Number(targetPrice) || 0
  const targetMovePct = tokenPriceUsd && targetNumber > 0 ? ((targetNumber - tokenPriceUsd) / tokenPriceUsd) * 100 : undefined
  const targetMarketCapUsd = tokenPriceUsd && tokenMarketCapUsd && targetNumber > 0 ? (tokenMarketCapUsd * targetNumber) / tokenPriceUsd : undefined
  const amountNumber = Number(amount) || 0
  const ordersNumber = Math.max(1, Math.floor(Number(orders) || 0))
  const intervalSeconds = Math.max(1, Math.floor(Number(every) || 0)) * UNIT_SECONDS[unit]
  const perOrderUsd = inputPriceUsd ? (amountNumber / ordersNumber) * inputPriceUsd : undefined
  // what the randomizers will actually produce (John, Oct 30 2026): the order range in dollars, the wait range in time
  const perOrderBase = amountNumber / ordersNumber
  const amountPreview =
    perOrderBase > 0
      ? inputPriceUsd
        ? `${formatUsd(perOrderBase * (1 - amountVariance / 100) * inputPriceUsd)} - ${formatUsd(perOrderBase * (1 + amountVariance / 100) * inputPriceUsd)}`
        : `${formatTokenAmount(perOrderBase * (1 - amountVariance / 100), inputSymbol)} - ${formatTokenAmount(perOrderBase * (1 + amountVariance / 100), inputSymbol)} ${inputSymbol}`
      : undefined
  const intervalPreview = `${formatDuration(Math.round(intervalSeconds * (1 - intervalVariance / 100)))} - ${formatDuration(Math.round(intervalSeconds * (1 + intervalVariance / 100)))}`

  const refreshActivity = useRefreshActivity()
  const create = useMutation(
    trpc.strategy.create.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: trpc.strategy.list.queryKey() })
        refreshActivity(['strategy', 'position'])
        reset()
        onClose()
      },
      onError: (err) => setError(errorMessage(err)),
    }),
  )

  function reset() {
    setTargetPrice('')
    setStep(1)
    setName('')
    setSide('sell')
    setMethod('dca')
    setTokenAddress('')
    setSettlement('SOL')
    setAmount('')
    setEvery('5')
    setUnit('minute')
    setOrders('10')
    setOptionalOpen(false)
    setMaxImpact(10)
    setMarketCapOn(false)
    setMinMarketCap('')
    setMaxMarketCap('')
    setRandomizeAmount(false)
    setAmountVariance(DEFAULT_VARIANCE_PCT)
    setRandomizeInterval(false)
    setIntervalVariance(DEFAULT_VARIANCE_PCT)
    setScheduledOn(false)
    setStartAt('')
    setError(null)
  }

  function pickSettlement(t: PickedToken) {
    setSettlement(t.symbol.toUpperCase() as Settlement)
    if (side === 'buy') setAmount('')
  }

  // the range as typed, read back in words beside the setting's title (John, Nov 2 2026: shorthand like 2.5m or 600k)
  const minMcTyped = parseUsdShorthand(minMarketCap)
  const maxMcTyped = parseUsdShorthand(maxMarketCap)
  const marketCapPreview =
    minMcTyped === null || maxMcTyped === null || (minMcTyped === undefined && maxMcTyped === undefined) ? undefined : formatMarketCapRange(minMcTyped, maxMcTyped)
  function submit() {
    setError(null)
    const raw = toRaw(amount, inputDecimals)
    if (!tokenAddress) return setError(side === 'sell' ? 'Pick a token to sell.' : 'Enter the token to buy.')
    if (!raw) return setError('Enter an amount.')
    if (amountNumber > inputBalance + 1e-9) return setError('Amount is more than your balance.')
    if (privateBuy && amountNumber > inputBalance - (poolMode ? 0.02 : 0.01) + 1e-9) return setError(poolMode ? 'Leave about 0.02 SOL extra for the network fees.' : 'Leave about 0.01 SOL extra for the private routing and fees.')
    if (privateBuy && settlement !== 'SOL') return setError('Buying a token with USDC or USDT is not available yet; pay with SOL.')
    const minMc = marketCapOn ? minMcTyped : undefined
    const maxMc = marketCapOn ? maxMcTyped : undefined
    if (minMc === null || maxMc === null) return setError('Enter the market cap as a figure, like 2.5m or 600k.')
    if (marketCapOn && minMc === undefined && maxMc === undefined) return setError('Enter a market cap minimum, a maximum, or switch the range off.')
    if (minMc !== undefined && maxMc !== undefined && minMc >= maxMc) return setError('The market cap minimum must be below the maximum.')
    if (method === 'limit') {
      if (!(targetNumber > 0)) return setError('Enter the target price.')
      if (tokenPriceUsd && side === 'sell' && targetNumber <= tokenPriceUsd) return setError('The target is at or below the current price; use Sell instead.')
      if (tokenPriceUsd && side === 'buy' && targetNumber >= tokenPriceUsd) return setError('The target is at or above the current price; use Buy instead.')
    }
    if (scheduledOn && !startAt) return setError('Pick the start date and time, or switch the scheduled start off.')
    if (scheduledOn && new Date(startAt).getTime() <= Date.now()) return setError('The scheduled start is in the past.')
    create.mutate({
      walletId: wallet.id,
      side,
      tokenAddress,
      settlement,
      totalAmountRaw: raw.toString(),
      kind: method === 'limit' ? 'limit' : 'dca',
      limit: method === 'limit' ? { targetPriceUsd: targetNumber } : undefined,
      orders: method === 'limit' ? 1 : ordersNumber,
      intervalSeconds: method === 'limit' ? 60 : intervalSeconds,
      name: name.trim() || undefined,
      exchange: onExchange ? exchangeId : undefined,
      tokenSymbol: side === 'sell' ? selectedHolding?.symbol : (lookup.data?.symbol ?? undefined),
      limits: { maxPriceImpactPct: maxImpact, minMarketCapUsd: minMc, maxMarketCapUsd: maxMc },
      execution: method === 'limit' ? { randomizeAmount: false, randomizeInterval: false } : {
        randomizeAmount,
        amountVariancePct: randomizeAmount ? amountVariance : undefined,
        randomizeInterval,
        intervalVariancePct: randomizeInterval ? intervalVariance : undefined,
        startAt: scheduledOn && startAt ? new Date(startAt).toISOString() : undefined,
      },
      // route is always auto: the server defaults it and the choice is not shown (John, Oct 29 2026)
      sourceVault: side === 'sell' && fromVault,
    })
  }

  const title = step === 1 ? 'Create strategy' : `${method === 'limit' ? 'Limit' : 'DCA'} ${side === 'sell' ? 'Sell' : 'Buy'}`
  const footer =
    step === 1 ? (
      <button type="button" className="btn btn-primary w-full" disabled={method === 'volume'} onClick={() => setStep(2)}>
        Continue
      </button>
    ) : (
      <div className="flex gap-3">
        <button type="button" className="btn btn-secondary flex-1" onClick={() => setStep(1)}>
          Back
        </button>
        <button type="button" className="btn btn-primary flex-1" disabled={create.isPending} onClick={submit}>
          {create.isPending ? <Spinner /> : 'Set strategy'}
        </button>
      </div>
    )

  const settlementChips = (
    <div className="flex flex-wrap gap-2">
      {settlements.map((t) => {
        const symbol = t.symbol.toUpperCase()
        const lockedToSol = privateBuy && symbol !== 'SOL'
        return (
          <button
            key={t.address}
            type="button"
            className="chip flex items-center gap-2 !pl-1.5"
            aria-pressed={settlement === symbol}
            disabled={lockedToSol}
            title={lockedToSol ? 'Token buys pay with SOL for now' : symbol}
            onClick={() => pickSettlement(t)}
          >
            <TokenIcon symbol={symbol} imageUrl={t.imageUrl} size="size-5" />
            {symbol}
          </button>
        )
      })}
    </div>
  )

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose() }}
      title={
        <span className="flex items-center gap-3">
          {title}
          {/* the chain, in the header beside the title, as on Swap */}
          <ChainChip imageUrl={tokenLogo(SOL_MINT)} />
          {/* the exchange this strategy belongs to, every one on offer with the pick lit (John, Oct 31 2026: its logo; Nov 2 2026: "for create strategy do same thing" as the swap's group) */}
          {onExchange ? (
            <>
              <HeaderRule />
              <PlatformGroup value={exchangeId} options={exchangeOptions} onChange={(id) => setExchangeId(id as ExchangeId)} />
            </>
          ) : null}
        </span>
      }
      footer={footer}
      width="lg"
    >
      {step === 1 ? (
        <div className="grid gap-5">
          <label className="grid gap-1.5 text-sm">
            <span className="text-muted">Strategy name <span className="text-xs">(optional)</span></span>
            <div className="control flex items-center px-3">
              <input
                className="text-base sm:text-sm w-full bg-transparent py-2.5 outline-none"
                maxLength={24}
                placeholder="Strategy name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <span className="text-xs text-muted">{name.length}/24</span>
            </div>
          </label>
          <div className="grid gap-1.5 text-sm">
            <span className="text-muted">Order type</span>
            <Segmented value={side} onChange={setSide} options={[{ value: 'sell', label: 'Sell' }, { value: 'buy', label: 'Buy' }]} />
          </div>
          <div className="grid gap-2 text-sm">
            <span className="text-muted">Strategy method</span>
            {(
              [
                { value: 'dca', label: 'DCA', sub: 'TWAP', desc: 'Gradually enter or exit positions over time', soon: false },
                { value: 'limit', label: 'Limit', sub: 'Price target', desc: 'Sell or buy the whole amount the moment the price reaches your target', soon: false },
                { value: 'volume', label: 'Volume', sub: 'VWAP', desc: 'Trade based on real-time market activity', soon: true },
              ] as const
            ).map((option) => (
              <button
                key={option.value}
                type="button"
                disabled={option.soon}
                onClick={() => setMethod(option.value)}
                className={`control flex items-center gap-3 px-4 py-3 text-left disabled:opacity-50 ${
                  method === option.value ? '!border-accent' : ''
                }`}
              >
                <span
                  className={`size-4 rounded-full border-2 ${method === option.value ? 'border-accent bg-accent' : 'border-muted'}`}
                />
                <span className="flex-1">
                  <span className="font-medium">{option.label}</span>{' '}
                  <span className="text-xs text-muted">{option.sub}</span>
                  <span className="block text-xs text-muted">{option.desc}</span>
                </span>
                {option.soon ? <span className="text-xs text-muted">Soon</span> : null}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid gap-5">
          <div className="card grid gap-3 p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">{side === 'sell' ? 'Sell' : 'Buy with'}</span>
              <span className="flex gap-2 text-xs text-accent">
                {[25, 50, 100].map((pct) => (
                  <button key={pct} type="button" onClick={() => setAmount(trimAmount((inputBalance * pct) / 100))}>
                    {pct === 100 ? 'Max' : `${pct}%`}
                  </button>
                ))}
              </span>
            </div>
            <div className="control flex items-center gap-2 px-3">
              <NumericInput
                className="w-full bg-transparent py-3 text-lg outline-none"
                placeholder="0.00"
                value={amount}
                onChange={setAmount}
              />
              {side === 'sell' ? (
                <TokenPicker
                  value={selectedHolding ? holdingToPicked(selectedHolding) : null}
                  onChange={(t) => { setTokenAddress(t.address); setAmount('') }}
                  holdings={sellable}
                  allowSearch={false}
                  heldOnly
                  showSymbol
                  onlyHoldings
                />
              ) : (
                <span className="flex items-center gap-2 rounded-control bg-surface px-2 py-1.5 text-sm">
                  <TokenIcon symbol={settlement} imageUrl={settlementToken?.imageUrl} size="size-5" />
                  {settlement}
                </span>
              )}
            </div>
            <div className="flex justify-between text-xs text-muted">
              <span>{inputPriceUsd ? formatUsd(amountNumber * inputPriceUsd) : '$–'}</span>
              <span>Balance {formatTokenAmount(inputBalance, inputSymbol)}{inputSymbol ? ` ${inputSymbol}` : ''}</span>
            </div>
            {side === 'sell' && sellable.length === 0 ? (
              <p className="text-xs text-muted">Nothing to sell yet.</p>
            ) : null}
            <div className="border-t border-line pt-3 text-sm">
              <div className="mb-2 font-medium">{side === 'sell' ? 'Receive' : 'Pay with'}</div>
              {settlementChips}
              {side === 'buy' ? (
                <div className="mt-3 grid gap-1">
                  <div className="mb-1 font-medium">Receive</div>
                  <div className="control flex items-center px-3">
                    <input
                      className="w-full bg-transparent py-2.5 font-mono text-base sm:text-sm outline-none"
                      placeholder="Token mint address"
                      value={tokenAddress}
                      onChange={(e) => setTokenAddress(e.target.value.trim())}
                    />
                  </div>
                  <div className="text-xs text-muted">
                    {lookup.isFetching ? 'Looking up…' : lookup.data ? `${(lookup.data.symbol ?? '').toUpperCase()} ${lookup.data.name ?? ''} at ${formatUsd(lookup.data.priceUsd)}` : lookup.isError ? 'Token not found' : 'Paste the mint address of the token to buy'}
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {method === 'limit' ? (
            <div className="card grid gap-3 p-4">
              <label className="grid gap-1.5 text-sm">
                <span className="text-muted">{side === 'sell' ? 'Sell everything when the price reaches' : 'Buy when the price falls to'}</span>
                <div className="control flex items-center px-3">
                  <span className="text-muted">$</span>
                  <NumericInput className="text-base sm:text-sm w-full bg-transparent py-2.5 pl-1 outline-none" placeholder="0.00" value={targetPrice} onChange={setTargetPrice} />
                </div>
              </label>
              <div className="control grid gap-1 px-3 py-2 text-xs">
                <div className="flex justify-between"><span className="text-muted">Price now</span><span>{tokenPriceUsd ? formatPrice(tokenPriceUsd) : '$–'}</span></div>
                <div className="flex justify-between"><span className="text-muted">Target</span><span>{targetNumber > 0 ? `${formatPrice(targetNumber)}${targetMovePct !== undefined ? ` (${targetMovePct > 0 ? '+' : ''}${targetMovePct.toFixed(1)}%)` : ''}` : '–'}</span></div>
                <div className="flex justify-between"><span className="text-muted">Market cap at target</span><span>{targetMarketCapUsd !== undefined ? formatUsd(targetMarketCapUsd, true) : '–'}</span></div>
              </div>
              <p className="text-xs text-muted">Checked every 30 seconds. The whole amount goes in one order, from every wallet holding it at once.</p>
            </div>
          ) : (
          <div className="card grid gap-3 p-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <label className="grid gap-1.5">
                <span className="text-muted">{side === 'sell' ? 'Sell every' : 'Buy every'}</span>
                <div className="control flex items-center px-3">
                  <NumericInput mode="integer" className="text-base sm:text-sm w-full bg-transparent py-2.5 outline-none" value={every} onChange={setEvery} />
                  <select className="bg-transparent text-base sm:text-sm" value={unit} onChange={(e) => setUnit(e.target.value as Unit)}>
                    <option value="minute">Minute</option>
                    <option value="hour">Hour</option>
                    <option value="day">Day</option>
                  </select>
                </div>
              </label>
              <label className="grid gap-1.5">
                <span className="text-muted">Over</span>
                <div className="control flex items-center px-3">
                  <NumericInput mode="integer" className="text-base sm:text-sm w-full bg-transparent py-2.5 outline-none" value={orders} onChange={setOrders} />
                  <span className="text-sm text-muted">Orders</span>
                </div>
              </label>
            </div>
            <div className="control grid gap-1 px-3 py-2 text-xs">
              <div className="flex justify-between"><span className="text-muted">Avg. value per order</span><span>{perOrderUsd !== undefined ? formatUsd(perOrderUsd) : `${formatAmount(amountNumber / ordersNumber)} ${inputSymbol}`}</span></div>
              <div className="flex justify-between"><span className="text-muted">Total execution time</span><span>{formatDuration(intervalSeconds * ordersNumber)}</span></div>
            </div>
          </div>
          )}
          <div>
            {/* one expander, every optional setting inside, in John's order (Nov 1 2026): size, frequency, price impact, market cap, scheduled start */}
            <button type="button" className="flex w-full items-center justify-between py-1 text-left" onClick={() => setOptionalOpen((open) => !open)} aria-expanded={optionalOpen}>
              <span className="font-medium">Optional settings</span>
              <ChevronDown className={`size-4 shrink-0 text-muted transition-transform ${optionalOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>
            {optionalOpen ? (
              <div className="mt-2 grid gap-3">
                {method !== 'limit' ? (
                  <>
                    <Setting title="Randomize order size" preview={amountPreview} on={randomizeAmount} onToggle={setRandomizeAmount} hint="Each order is sized at random within the chosen range of the base size; the total stays the same.">
                      <PercentSlider value={amountVariance} min={1} max={100} onChange={setAmountVariance} />
                    </Setting>
                    <Setting title="Randomize frequency" preview={intervalPreview} on={randomizeInterval} onToggle={setRandomizeInterval} hint="The wait between orders moves at random within the chosen range of the interval.">
                      <PercentSlider value={intervalVariance} min={1} max={100} onChange={setIntervalVariance} />
                    </Setting>
                  </>
                ) : null}
                <Setting title="Price impact threshold" hint="Orders whose quoted price impact exceeds this are held back and retried at the next interval.">
                  <PercentSlider value={maxImpact} min={1} max={50} onChange={setMaxImpact} />
                </Setting>
                {method !== 'limit' ? (<Setting title="Market cap range" preview={marketCapPreview} on={marketCapOn} onToggle={setMarketCapOn} hint="Orders only run while the token's market cap is inside the range; otherwise they are held back and retried at the next interval. Type figures like 2.5m or 600k.">
                  <div className="grid grid-cols-2 gap-3">
                    <label className="grid gap-1.5">
                      <span className="text-xs text-muted">Minimum</span>
                      <div className="control flex items-center px-3"><span className="text-muted">$</span><NumericInput mode="shorthand" className="text-base sm:text-sm w-full bg-transparent py-2 pl-1 outline-none" placeholder="e.g. 2.5m" value={minMarketCap} onChange={setMinMarketCap} /></div>
                    </label>
                    <label className="grid gap-1.5">
                      <span className="text-xs text-muted">Maximum</span>
                      <div className="control flex items-center px-3"><span className="text-muted">$</span><NumericInput mode="shorthand" className="text-base sm:text-sm w-full bg-transparent py-2 pl-1 outline-none" placeholder="e.g. 2.5m" value={maxMarketCap} onChange={setMaxMarketCap} /></div>
                    </label>
                  </div>
                </Setting>) : null}
                {method !== 'limit' ? (
                  <Setting title="Scheduled start" on={scheduledOn} onToggle={setScheduledOn} hint="The first order runs at this date and time.">
                    <input type="datetime-local" className="text-base sm:text-sm control w-full px-3 py-2" value={startAt} onChange={(e) => setStartAt(e.target.value)} />
                  </Setting>
                ) : null}
              </div>
            ) : null}
          </div>
          {error ? <p className="text-sm text-negative">{error}</p> : null}
        </div>
      )}
    </Modal>
  )
}

/** A Sonar-style optional setting: a title row with a switch, and its controls once it is on. */
function Setting({
  title,
  hint,
  preview,
  on,
  onToggle,
  children,
}: {
  title: string
  hint?: string
  /** the range the setting produces, shown beside the title while it is on */
  preview?: string
  /** omit for a setting that is always on */
  on?: boolean
  onToggle?: (on: boolean) => void
  children: React.ReactNode
}) {
  const alwaysOn = on === undefined
  return (
    <div className="card grid gap-3 p-4 text-sm">
      <div className="flex items-center justify-between gap-4">
        <span className="font-medium">{title}</span>
        <span className="flex items-center gap-3">
          {preview && (alwaysOn || on) ? <span className="text-muted tabular-nums">{preview}</span> : null}
          {!alwaysOn && onToggle ? <Toggle checked={on} onChange={onToggle} label={title} /> : null}
        </span>
      </div>
      {alwaysOn || on ? (
        <>
          {hint ? <p className="text-xs text-muted">{hint}</p> : null}
          {children}
        </>
      ) : null}
    </div>
  )
}

function PercentSlider({ value, min, max, onChange }: { value: number; min: number; max: number; onChange: (value: number) => void }) {
  return (
    <div className="flex items-center gap-3">
      <input type="range" min={min} max={max} value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-accent" />
      <span className="control w-16 shrink-0 px-2 py-1 text-center tabular-nums">{value}%</span>
    </div>
  )
}
